import type { Customer, CustomerDetailsPayload } from "../../../../../types";

export interface BuildConfirmedCustomerDataParams {
    verificationType: "phoneNumber" | "billingAccountNumberAndPin";
    mdn: string;
    additionalDetailsMdn: string;
    customerName: string;
    customerData: CustomerDetailsPayload | undefined;
    showAdditionalDetailsEmailInput: boolean;
    additionalDetailsEmail: string;
    showAdditionalDetailsOrderIdInput: boolean;
    additionalDetailsOrderId: string;
    showAdditionalDetailsAccountNumberInput: boolean;
    additionalDetailsAccountNumber: string;
    billingAccountNumber: string;
    currentSubscriptions: string[] | undefined;
}

export interface ConfirmedCustomerData {
    confirmedMdn: string;
    confirmedEmail: string | undefined;
    confirmedOrderId: string | undefined;
    confirmedAccountNumber: string | undefined;
    confirmedAdditionalDetailsAccountNumber: string | undefined;
    customerToSave: Partial<Customer> & { fullName: string; mdn: string };
}

/**
 * Builds the confirmed customer data object based on verification type and form inputs.
 * Resolves which values to use based on the verification flow.
 */
export function buildConfirmedCustomerData(params: BuildConfirmedCustomerDataParams): ConfirmedCustomerData {
    const {
        verificationType,
        mdn,
        additionalDetailsMdn,
        customerName,
        customerData,
        showAdditionalDetailsEmailInput,
        additionalDetailsEmail,
        showAdditionalDetailsOrderIdInput,
        additionalDetailsOrderId,
        showAdditionalDetailsAccountNumberInput,
        additionalDetailsAccountNumber,
        billingAccountNumber,
        currentSubscriptions,
    } = params;

    const confirmedMdn = verificationType === "phoneNumber" ? mdn : additionalDetailsMdn;

    const confirmedEmail = showAdditionalDetailsEmailInput
        ? additionalDetailsEmail
        : customerData?.primaryAccountHolder?.email;

    const confirmedOrderId =
        showAdditionalDetailsOrderIdInput && additionalDetailsOrderId.length > 0 ? additionalDetailsOrderId : undefined;

    const confirmedAdditionalDetailsAccountNumber =
        showAdditionalDetailsAccountNumberInput && additionalDetailsAccountNumber.length > 0
            ? additionalDetailsAccountNumber
            : undefined;

    const confirmedAccountNumber =
        verificationType === "billingAccountNumberAndPin"
            ? billingAccountNumber
            : customerData?.primaryAccountHolder?.accountNumber;

    // Prefer the agent-confirmed mdn (form input); fall back to whatever the
    // partner lookup returned. Default to "" so downstream `!!customer.mdn`
    // gates behave consistently when neither source has a value.
    const customerToSave = {
        ...customerData?.primaryAccountHolder,
        currentSubscriptions,
        fullName: customerName,
        email: confirmedEmail,
        mdn: confirmedMdn || customerData?.primaryAccountHolder?.mdn || "",
        orderId: confirmedOrderId,
        accountNumber: confirmedAccountNumber,
    };

    return {
        confirmedMdn,
        confirmedEmail,
        confirmedOrderId,
        confirmedAccountNumber,
        confirmedAdditionalDetailsAccountNumber,
        customerToSave,
    };
}
